import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  Avatar,
  Container,
  InputBase,
  Paper,
  Popper,
  Fade,
  ClickAwayListener,
  CircularProgress,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  Assignment as AssignmentIcon,
  PlayArrow as PlayArrowIcon,
  Assessment as AssessmentIcon,
  AccountTree as AccountTreeIcon,
  LocalOffer as LocalOfferIcon,
  People as PeopleIcon,
  Settings as SettingsIcon,
  AccountCircle,
  Logout,
  Search as SearchIcon,
  Close as CloseIcon,
  BugReport as BugIcon,
  ConfirmationNumber as TicketIcon,
  Description as StoryIcon,
  Rocket as ReleaseIcon,
  GridView as ModulesIcon,
  AutoAwesome as AIIcon,
  ArrowForward as ArrowForwardIcon,
  Storage as StorageIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import CentimeLogo from '../centime_logo.svg';
import api from '../services/api';
import QAssureChatFloating from './QAssureChatFloating';
import NavContext from '../context/NavContext';

const drawerWidth = 240;

// Quick search navigation options
const quickSearchOptions = [
  { label: 'Test Cases', path: '/test-cases', icon: AssignmentIcon, keywords: ['test', 'case', 'tc', 'tests'] },
  { label: 'Test Design Studio', path: '/test-design-studio', icon: PlayArrowIcon, keywords: ['design', 'studio', 'create', 'new test', 'write test', 'bdd', 'gherkin'] },
  { label: 'Issues', path: '/issues', icon: BugIcon, keywords: ['issue', 'bug', 'defect', 'problem'] },
  { label: 'Production Tickets', path: '/production-tickets', icon: TicketIcon, keywords: ['production', 'prod', 'ticket', 'jira', 'CN-', 'BO'] },
  { label: 'Stories', path: '/stories', icon: StoryIcon, keywords: ['story', 'stories', 'jira', 'user story'] },
  { label: 'Releases', path: '/releases', icon: ReleaseIcon, keywords: ['release', 'version', 'deploy'] },
  { label: 'Dashboard', path: '/dashboard', icon: DashboardIcon, keywords: ['dashboard', 'home', 'overview'] },
  { label: 'Modules', path: '/modules', icon: ModulesIcon, keywords: ['module', 'modules', 'component'] },
  { label: 'Reports', path: '/reports', icon: AssessmentIcon, keywords: ['report', 'reports', 'analytics'] },
  { label: 'Coverage Matrix', path: '/coverage', icon: AssessmentIcon, keywords: ['coverage', 'matrix', 'automation', 'traceability', 'gaps'] },
  { label: 'Execute Tests', path: '/executions', icon: PlayArrowIcon, keywords: ['execute', 'run', 'execution', 'test run'] },
  { label: 'GL Data Generator', path: '/gl-data-generator', icon: StorageIcon, keywords: ['gl', 'data', 'generator', 'quickbooks', 'qbo', 'invoice', 'bill', 'test data', 'netsuite', 'sage'] },
];

// Icon mapping for intents
const intentIcons = {
  view_test_cases: AssignmentIcon,
  view_test_design_studio: PlayArrowIcon,
  view_issues: BugIcon,
  view_stories: StoryIcon,
  view_release: ReleaseIcon,
  view_release_dashboard: DashboardIcon,
  view_modules: ModulesIcon,
  view_reports: AssessmentIcon,
  view_dashboard: DashboardIcon,
  view_executions: PlayArrowIcon,
  unknown: SearchIcon,
};

// Normalize path
const normalizePath = (path) => {
  if (!path) return path;
  const pathMap = {
    '/test_cases': '/test-cases',
    '/test-cases': '/test-cases',
    '/jira_stories': '/stories',
    '/jira-stories': '/stories',
  };
  const basePath = path.split('?')[0];
  if (pathMap[basePath]) {
    return path.replace(basePath, pathMap[basePath]);
  }
  return path.replace(/_/g, '-');
};

const Layout = ({ children }) => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [navCollapsed, setNavCollapsed] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // Search state
  const searchInputRef = useRef(null);
  const searchContainerRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchOpen, setSearchOpen] = useState(false);
  const [isAISearching, setIsAISearching] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [filteredOptions, setFilteredOptions] = useState(quickSearchOptions);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const abortControllerRef = useRef(null);

  // Keyboard shortcut for Cmd+K / Ctrl+K
  const handleKeyDown = useCallback((event) => {
    if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
      event.preventDefault();
      searchInputRef.current?.focus();
    }
  }, []);

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [handleKeyDown]);

  // Filter quick search options based on query - only show matching page navigation
  useEffect(() => {
    if (searchQuery.trim().length < 1) {
      setFilteredOptions([]);
      setSelectedIndex(0);
      return;
    }

    const queryLower = searchQuery.toLowerCase();
    
    // Remove common navigation phrases to get the core intent
    const cleanQuery = queryLower
      .replace(/^(go to|navigate to|open|show|view|take me to)\s+/i, '')
      .replace(/\s+(page|section|screen)$/i, '')
      .trim();
    
    // Only show navigation options that directly match the query
    // Match if: query contains keyword OR keyword contains query OR cleaned query matches
    const filtered = quickSearchOptions.filter(option => {
      const labelLower = option.label.toLowerCase();
      return (
        labelLower.includes(queryLower) ||
        labelLower.includes(cleanQuery) ||
        queryLower.includes(labelLower) ||
        cleanQuery.includes(labelLower) ||
        option.keywords.some(kw => 
          kw.includes(queryLower) || 
          queryLower.includes(kw) ||
          kw.includes(cleanQuery) ||
          cleanQuery.includes(kw)
        )
      );
    });

    // Only show filtered navigation options if there's a direct match
    // Don't show generic "Search X for..." options - that's what AI search is for
    setFilteredOptions(filtered);
    setSelectedIndex(0);
  }, [searchQuery]);

  // AI Search function
  const performAISearch = useCallback(async () => {
    if (!searchQuery || searchQuery.trim().length < 3) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    abortControllerRef.current = new AbortController();
    setIsAISearching(true);
    setAiResult(null);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setIsAISearching(false);
        return;
      }

      const response = await api.post('/search/smart', { query: searchQuery.trim() }, {
        signal: abortControllerRef.current.signal,
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' }
      });
      
      setAiResult(response.data);
    } catch (err) {
      if (err.name !== 'AbortError' && err.code !== 'ERR_CANCELED') {
        console.error('AI search error:', err);
      }
    } finally {
      setIsAISearching(false);
    }
  }, [searchQuery]);

  // Handle search input change
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchOpen(value.length > 0);
    setAiResult(null);
  };

  // Handle quick navigate
  const handleQuickNavigate = (option) => {
    let path = option.path;
    if (option.searchQuery) {
      path += `?search=${encodeURIComponent(option.searchQuery)}`;
    }
    navigate(path);
    handleSearchClose();
  };

  // Handle AI result navigation
  const handleAINavigate = () => {
    if (aiResult && aiResult.success && aiResult.navigate_to) {
      let path = normalizePath(aiResult.navigate_to);
      if (aiResult.query_params && Object.keys(aiResult.query_params).length > 0) {
        const params = new URLSearchParams(aiResult.query_params);
        // Check if path already has query params
        const separator = path.includes('?') ? '&' : '?';
        path += `${separator}${params.toString()}`;
      }
      handleSearchClose();
      setTimeout(() => navigate(path), 50);
    }
  };

  // Handle search close
  const handleSearchClose = () => {
    setSearchOpen(false);
    setSearchQuery('');
    setAiResult(null);
    setSelectedIndex(0);
  };

  // Handle search keyboard navigation
  const handleSearchKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => prev < filteredOptions.length - 1 ? prev + 1 : prev);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : 0);
    } else if (e.key === 'Enter') {
      e.preventDefault();
      // If we already have an AI result, navigate to it
      if (aiResult && aiResult.success) {
        handleAINavigate();
      } else if (searchQuery.trim().length >= 3) {
        // Otherwise, trigger AI search first
        performAISearch();
      } else if (filteredOptions[selectedIndex]) {
        // Fallback to quick navigate if query is too short
        handleQuickNavigate(filteredOptions[selectedIndex]);
      }
    } else if (e.key === 'Escape') {
      handleSearchClose();
      searchInputRef.current?.blur();
    }
  };

  // Get icon for intent
  const getIntentIcon = (intent) => {
    const IconComponent = intentIcons[intent] || SearchIcon;
    return <IconComponent fontSize="small" />;
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleClose();
    logout();
    navigate('/login');
  };

  const menuItems = [
    { text: 'Dashboard', icon: <DashboardIcon sx={{ color: '#42a5f5' }} />, path: '/dashboard' },
    { text: 'Test Design Studio', icon: <PlayArrowIcon sx={{ color: '#ab47bc' }} />, path: '/test-design-studio' },
    { text: 'Test Cases', icon: <AssignmentIcon sx={{ color: '#66bb6a' }} />, path: '/test-cases' },
    { text: 'Stories', icon: <AssignmentIcon sx={{ color: '#ffa726' }} />, path: '/stories' },
    { text: 'Modules', icon: <AccountTreeIcon sx={{ color: '#26c6da' }} />, path: '/modules' },
    { text: 'Releases', icon: <LocalOfferIcon sx={{ color: '#ef5350' }} />, path: '/releases' },
    { text: 'Issues', icon: <BugIcon sx={{ color: '#e91e63' }} />, path: '/issues' },
    { text: 'Reports', icon: <AssessmentIcon sx={{ color: '#5c6bc0' }} />, path: '/reports' },
    { text: 'Coverage Matrix', icon: <AssessmentIcon sx={{ color: '#8e24aa' }} />, path: '/coverage' },
    { text: 'Execute Tests', icon: <PlayArrowIcon sx={{ color: '#4caf50' }} />, path: '/executions' },
    { text: 'Production Tickets', icon: <TicketIcon sx={{ color: '#ff7043' }} />, path: '/production-tickets' },
    { text: 'Users', icon: <PeopleIcon sx={{ color: '#29b6f6' }} />, path: '/users' },
    { text: 'Settings', icon: <SettingsIcon sx={{ color: '#78909c' }} />, path: '/settings' },
    { text: 'GL Data Generator', icon: <StorageIcon sx={{ color: '#8d6e63' }} />, path: '/gl-data-generator' },
    { text: 'API & SQL Library', icon: <StorageIcon sx={{ color: '#5c6bc0' }} />, path: '/template-library' },
  ];

  const drawer = (
    <div>
      <Toolbar
        sx={{
          cursor: 'pointer',
          bgcolor: 'primary.main',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          '&:hover': {
            bgcolor: 'primary.dark',
          }
        }}
        onClick={() => navigate('/dashboard')}
      >
        <Box
          component="img"
          src={CentimeLogo}
          alt="Centime Logo"
          sx={{
            height: 32,
            width: 'auto',
            objectFit: 'contain',
          }}
        />
      </Toolbar>
      <List>
        {menuItems.map((item) => (
          <ListItem key={item.text} disablePadding>
            <ListItemButton
              selected={location.pathname === item.path}
              onClick={() => {
                navigate(item.path);
                setMobileOpen(false);
              }}
            >
              <ListItemIcon>{item.icon}</ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButton>
          </ListItem>
        ))}
      </List>

      {/* QAssure AI — compact pill button (matches brand HTML design) */}
      <Box sx={{ px: 1.5, mt: 1, mb: 2 }}>
        <Box
          onClick={() => { navigate('/qassure-ai'); setMobileOpen(false); }}
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 2.5,
            py: 1.2,
            borderRadius: '50px',
            cursor: 'pointer',
            background: location.pathname === '/qassure-ai'
              ? 'linear-gradient(135deg, #1a2d4a, #1e3550)'
              : 'linear-gradient(135deg, #152438, #1a2c42)',
            border: location.pathname === '/qassure-ai'
              ? '1px solid rgba(0,212,255,0.45)'
              : '1px solid rgba(0,212,255,0.2)',
            boxShadow: location.pathname === '/qassure-ai'
              ? '0 0 18px rgba(0,212,255,0.25), inset 0 0 12px rgba(0,212,255,0.06)'
              : '0 0 12px rgba(0,212,255,0.08)',
            transition: 'all 0.25s ease',
            '&:hover': {
              border: '1px solid rgba(0,212,255,0.4)',
              boxShadow: '0 0 22px rgba(0,212,255,0.2), inset 0 0 10px rgba(0,212,255,0.05)',
              background: 'linear-gradient(135deg, #1e3348, #223a52)',
            },
          }}
        >
          {/* Brain icon */}
          <svg
            viewBox="0 0 100 100"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            width={32}
            height={32}
            style={{ filter: 'drop-shadow(0 0 8px rgba(0,212,255,0.7))', flexShrink: 0 }}
          >
            <path d="M50 18 C38 18 28 26 26 37 C20 38 16 43 16 49 C16 54 19 58 23 60 C22 63 22 67 25 70 C28 73 32 74 36 73 C38 77 44 80 50 80 C56 80 62 77 64 73 C68 74 72 73 75 70 C78 67 78 63 77 60 C81 58 84 54 84 49 C84 43 80 38 74 37 C72 26 62 18 50 18Z" stroke="#00d4ff" strokeWidth="2.5" fill="rgba(0,212,255,0.06)" />
            <circle cx="34" cy="38" r="3.5" fill="#00d4ff" />
            <circle cx="50" cy="32" r="3.5" fill="#00d4ff" />
            <circle cx="66" cy="38" r="3.5" fill="#00d4ff" />
            <circle cx="44" cy="52" r="3.5" fill="#00aaff" />
            <circle cx="56" cy="52" r="3.5" fill="#00aaff" />
            <circle cx="50" cy="64" r="3.5" fill="#0088ff" />
            <line x1="34" y1="38" x2="50" y2="32" stroke="#00d4ff" strokeWidth="1.2" opacity="0.6" />
            <line x1="50" y1="32" x2="66" y2="38" stroke="#00d4ff" strokeWidth="1.2" opacity="0.6" />
            <line x1="34" y1="38" x2="44" y2="52" stroke="#00d4ff" strokeWidth="1.2" opacity="0.6" />
            <line x1="66" y1="38" x2="56" y2="52" stroke="#00d4ff" strokeWidth="1.2" opacity="0.6" />
            <line x1="44" y1="52" x2="50" y2="64" stroke="#00aaff" strokeWidth="1.2" opacity="0.6" />
            <line x1="56" y1="52" x2="50" y2="64" stroke="#00aaff" strokeWidth="1.2" opacity="0.6" />
          </svg>

          {/* Brand text */}
          <Box sx={{ display: 'flex', alignItems: 'baseline', minWidth: 0 }}>
            <Typography
              component="span"
              sx={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 900,
                color: '#00d4ff',
                lineHeight: 1,
                textShadow: '0 0 12px rgba(0,212,255,0.6)',
              }}
            >Q</Typography>
            <Typography
              component="span"
              sx={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '1.1rem',
                fontWeight: 900,
                color: '#e8f4ff',
                lineHeight: 1,
              }}
            >Assure</Typography>
            <Box
              component="span"
              sx={{
                fontFamily: "'Orbitron', sans-serif",
                fontSize: '0.55rem',
                fontWeight: 700,
                color: '#00d4ff',
                bgcolor: 'rgba(0,212,255,0.08)',
                border: '1px solid rgba(0,212,255,0.3)',
                borderRadius: '4px',
                px: 0.6,
                py: '2px',
                ml: 0.75,
                letterSpacing: '2px',
                lineHeight: 1,
                textShadow: '0 0 8px rgba(0,212,255,0.6)',
                boxShadow: '0 0 8px rgba(0,212,255,0.1)',
              }}
            >AI</Box>
          </Box>
        </Box>
      </Box>
    </div>
  );

  return (
    <NavContext.Provider value={{ collapsed: navCollapsed, setCollapsed: setNavCollapsed }}>
    <Box sx={{ display: 'flex' }}>
      <AppBar
        position="fixed"
        sx={{
          width: { sm: navCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
          ml: { sm: navCollapsed ? 0 : `${drawerWidth}px` },
          transition: 'width 0.3s ease, margin-left 0.3s ease',
        }}
      >
        <Toolbar sx={{ gap: 2 }}>
          {/* Left Section - Menu & Title */}
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 1, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            variant="h6"
            noWrap
            component="div"
            onClick={() => navigate('/')}
            sx={{ flexShrink: 0, cursor: 'pointer', '&:hover': { opacity: 0.85 } }}
          >
            Quality Assurance Portal
          </Typography>
          
          {/* Center Section - Inline Search Bar (NetSuite Style) */}
          <ClickAwayListener onClickAway={() => setSearchOpen(false)}>
            <Box 
              ref={searchContainerRef}
              sx={{ 
                flexGrow: 1, 
                display: 'flex', 
                justifyContent: 'center',
                position: 'relative',
                maxWidth: 500,
                mx: 'auto',
              }}
            >
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  width: '100%',
                  backgroundColor: '#fff',
                  borderRadius: 1,
                  border: searchOpen ? '2px solid #1976d2' : '1px solid #ccc',
                  transition: 'all 0.2s ease',
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', pl: 1.5, gap: 0.5 }}>
                  <SearchIcon sx={{ fontSize: 20, color: '#666' }} />
                  <AIIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                </Box>
                <InputBase
                  inputRef={searchInputRef}
                  placeholder="Ask AI to find or navigate..."
                  value={searchQuery}
                  onChange={handleSearchChange}
                  onKeyDown={handleSearchKeyDown}
                  onFocus={() => {
                    if (searchQuery.length > 0) setSearchOpen(true);
                  }}
                  sx={{
                    flex: 1,
                    px: 1,
                    py: 0.75,
                    fontSize: '0.95rem',
                    color: '#333',
                    '& input::placeholder': {
                      color: '#888',
                      opacity: 1,
                      fontStyle: 'italic',
                    },
                  }}
                />
                {searchQuery && (
                  <IconButton 
                    size="small" 
                    onClick={() => { setSearchQuery(''); setSearchOpen(false); setAiResult(null); }}
                    sx={{ color: '#666', p: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                )}
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  borderLeft: '1px solid #ddd',
                  px: 1,
                  gap: 0.5,
                }}>
                  {isAISearching ? (
                    <CircularProgress size={18} sx={{ color: '#1976d2' }} />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={performAISearch}
                      disabled={searchQuery.trim().length < 3}
                      sx={{ 
                        p: 0.5,
                        color: searchQuery.trim().length >= 3 ? '#1976d2' : '#ccc',
                        '&:hover': { backgroundColor: 'rgba(25, 118, 210, 0.1)' },
                      }}
                      title="AI Search (min 3 characters)"
                    >
                      <AIIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>

              {/* Dropdown Results - Only show when there are results to display */}
              <Popper
                open={searchOpen && (aiResult || filteredOptions.length > 0)}
                anchorEl={searchContainerRef.current}
                placement="bottom-start"
                transition
                sx={{ 
                  zIndex: 1300, 
                  width: searchContainerRef.current?.offsetWidth || 500,
                }}
              >
                {({ TransitionProps }) => (
                  <Fade {...TransitionProps} timeout={150}>
                    <Paper
                      elevation={8}
                      sx={{
                        mt: 0.5,
                        borderRadius: 1,
                        maxHeight: 400,
                        overflow: 'auto',
                        border: '1px solid #ddd',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      }}
                    >
                      {/* AI Search Result */}
                      {aiResult && (
                        <>
                          {aiResult.success ? (
                            <ListItemButton
                              onClick={handleAINavigate}
                              sx={{
                                py: 1,
                                px: 2,
                                backgroundColor: '#e3f2fd',
                                '&:hover': { backgroundColor: '#bbdefb' },
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1,
                              }}
                            >
                              <AIIcon sx={{ fontSize: 18, color: '#1976d2', flexShrink: 0 }} />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, minWidth: 0 }}>
                                <Typography 
                                  variant="body2" 
                                  fontWeight={500}
                                  sx={{ 
                                    color: '#1565c0',
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                  }}
                                >
                                  {aiResult.message}
                                </Typography>
                                {aiResult.entity_ids?.length > 0 && (
                                  <Typography 
                                    variant="caption" 
                                    sx={{ 
                                      color: '#666',
                                      whiteSpace: 'nowrap',
                                      flexShrink: 0,
                                    }}
                                  >
                                    ({aiResult.entity_ids.length} results)
                                  </Typography>
                                )}
                              </Box>
                              <ArrowForwardIcon sx={{ fontSize: 18, color: '#1976d2', flexShrink: 0 }} />
                            </ListItemButton>
                          ) : (
                            <Box sx={{ p: 1.5, px: 2, backgroundColor: '#fff3e0', display: 'flex', alignItems: 'center', gap: 1 }}>
                              <AIIcon sx={{ fontSize: 18, color: '#f57c00', flexShrink: 0 }} />
                              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                {aiResult.message}
                              </Typography>
                            </Box>
                          )}
                          <Divider />
                        </>
                      )}

                      {/* Quick Navigation Options - Only show when there are matches */}
                      {!aiResult && filteredOptions.length > 0 && (
                        <>
                          <Box sx={{ 
                            px: 2, 
                            py: 1, 
                            backgroundColor: '#f5f5f5',
                            borderBottom: '1px solid #e0e0e0',
                          }}>
                            <Typography variant="caption" sx={{ color: '#666', fontWeight: 500 }}>
                              Quick Navigation
                            </Typography>
                          </Box>
                          <List disablePadding dense>
                            {filteredOptions.map((option, index) => {
                              const IconComponent = option.icon;
                              return (
                                <ListItemButton
                                  key={index}
                                  selected={index === selectedIndex}
                                  onClick={() => handleQuickNavigate(option)}
                                  sx={{
                                    py: 1,
                                    '&.Mui-selected': { backgroundColor: '#e3f2fd' },
                                    '&:hover': { backgroundColor: '#f5f5f5' },
                                  }}
                                >
                                  <ListItemIcon sx={{ minWidth: 36 }}>
                                    <IconComponent 
                                      fontSize="small" 
                                      sx={{ color: '#666' }}
                                    />
                                  </ListItemIcon>
                                  <ListItemText
                                    primary={
                                      <Typography variant="body2">
                                        <span style={{ color: '#888' }}>Go to </span>
                                        <strong>{option.label}</strong>
                                      </Typography>
                                    }
                                  />
                                </ListItemButton>
                              );
                            })}
                          </List>
                        </>
                      )}

                      {/* Footer hint - only show when dropdown is visible */}
                      {(aiResult || filteredOptions.length > 0) && (
                        <Box sx={{ 
                          px: 2, 
                          py: 0.75, 
                          backgroundColor: '#fafafa',
                          borderTop: '1px solid #e0e0e0',
                          display: 'flex',
                          justifyContent: 'space-between',
                        }}>
                          <Typography variant="caption" color="text.secondary">
                            ↑↓ Navigate • Enter to search
                          </Typography>
                          <Typography variant="caption" color="primary">
                            Press Enter for AI Search
                          </Typography>
                        </Box>
                      )}
                    </Paper>
                  </Fade>
                )}
              </Popper>
            </Box>
          </ClickAwayListener>

          {/* Right Section - User Info */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <Typography variant="body2" sx={{ opacity: 0.9, display: { xs: 'none', md: 'block' } }}>
              {user?.email}
            </Typography>
            <IconButton
              size="large"
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              color="inherit"
            >
              <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                {user?.email?.charAt(0).toUpperCase()}
              </Avatar>
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              anchorOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              keepMounted
              transformOrigin={{
                vertical: 'top',
                horizontal: 'right',
              }}
              open={Boolean(anchorEl)}
              onClose={handleClose}
            >
              <MenuItem disabled>
                <ListItemIcon>
                  <AccountCircle fontSize="small" />
                </ListItemIcon>
                <ListItemText>
                  {user?.full_name || 'User'} ({user?.role})
                </ListItemText>
              </MenuItem>
              <Divider />
              <MenuItem onClick={handleLogout}>
                <ListItemIcon>
                  <Logout fontSize="small" />
                </ListItemIcon>
                <ListItemText>Logout</ListItemText>
              </MenuItem>
            </Menu>
          </Box>
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{
          width: { sm: navCollapsed ? 0 : drawerWidth },
          flexShrink: { sm: 0 },
          transition: 'width 0.3s ease',
        }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: navCollapsed ? 0 : drawerWidth,
              transition: 'width 0.3s ease',
              overflowX: 'hidden',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      {/* Floating nav toggle tab */}
      <Box
        onClick={() => setNavCollapsed((c) => !c)}
        sx={{
          position: 'fixed',
          left: navCollapsed ? 0 : drawerWidth,
          top: '50%',
          transform: 'translateY(-50%)',
          zIndex: 1201,
          transition: 'left 0.3s ease',
          display: { xs: 'none', sm: 'flex' },
          alignItems: 'center',
          justifyContent: 'center',
          width: 20,
          height: 44,
          bgcolor: 'primary.main',
          borderRadius: '0 8px 8px 0',
          cursor: 'pointer',
          boxShadow: '2px 0 6px rgba(0,0,0,0.15)',
          '&:hover': { bgcolor: 'primary.dark', width: 24 },
        }}
      >
        {navCollapsed
          ? <ChevronRightIcon sx={{ fontSize: 18, color: '#fff' }} />
          : <ChevronLeftIcon sx={{ fontSize: 18, color: '#fff' }} />
        }
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: navCollapsed ? 1 : 3,
          width: { sm: navCollapsed ? '100%' : `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          transition: 'all 0.3s ease',
        }}
      >
        <Container maxWidth="xl" disableGutters={navCollapsed}>{children}</Container>
      </Box>
      <QAssureChatFloating />
    </Box>
    </NavContext.Provider>
  );
};

export default Layout;
